import { useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { CloseButton } from "./CloseButton";
import { CloseIcon } from "../icons/CloseIcon";

export const ModalSheet = ({
  children,
  title,
  className,
  style,
  onClose,
  footer,
  hideCloseButton = false,
  closeButtonVariant = "primary",
  showHeaderCloseIcon = false,
  disableOutsideClick = true,
}: {
  children: React.ReactNode;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  onClose?: () => void;
  footer?: React.ReactNode;
  hideCloseButton?: boolean;
  closeButtonVariant?: "primary" | "secondary";
  showHeaderCloseIcon?: boolean;
  disableOutsideClick?: boolean;
}) => {
  useEffect(() => {
    if (!onClose) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex justify-center items-center bg-black/50 dark:bg-black/60 text-foreground p-3 sm:p-4 overflow-y-auto"
      onClick={disableOutsideClick ? undefined : onClose}
      data-testid="modal"
    >
      <div
        className={twMerge(
          "bg-[var(--color-card)] text-foreground border border-[var(--color-outline)] rounded-lg p-[15px] shadow-2xl w-full max-w-3xl max-h-[calc(100dvh-32px)] overflow-y-auto overscroll-contain min-w-0 select-none [&_input]:select-text [&_textarea]:select-text",
          className,
        )}
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex justify-between items-center">
            <h3 className="text-[13.5px] font-semibold leading-[21px] text-foreground">{title}</h3>
            {showHeaderCloseIcon && onClose && <CloseIcon onClick={onClose} />}
          </div>
        )}

        {children}
        {footer ? (
          footer
        ) : onClose && !hideCloseButton ? (
          <div className="flex justify-end mt-2">
            <CloseButton variant={closeButtonVariant} onClick={onClose} />
          </div>
        ) : null}
      </div>
    </div>
  );
};
